import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/**
 * Harmonic Armature adapter.
 * Draws diagonal lines connecting corners and edge midpoints,
 * creating a classical composition framework.
 *
 * The armature lines reveal natural focal points and movement
 * paths within a rectangular composition.
 */
export class HarmonicArmatureAdapter extends GridAdapter {
  readonly type = "harmonic-armature";
  readonly label = "Harmonic";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><line x1="1" y1="1" x2="15" y2="15" stroke="currentColor" stroke-opacity="0.4"/><line x1="15" y1="1" x2="1" y2="15" stroke="currentColor" stroke-opacity="0.4"/><line x1="1" y1="8" x2="8" y2="1" stroke="currentColor" stroke-opacity="0.3"/><line x1="8" y1="1" x2="15" y2="8" stroke="currentColor" stroke-opacity="0.3"/></svg>`;

  getDefaults(): Record<string, any> {
    return {
      level: "basic",
      color: "#4ae2c4",
      opacity: 0.3,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      {
        type: "select",
        key: "level",
        label: "Detail",
        default: "basic",
        options: [
          { value: "basic", label: "Basic (diagonals)" },
          { value: "mid", label: "Mid (+ midpoints)" },
          { value: "full", label: "Full armature" },
        ],
      },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#4ae2c4" },
      { type: "range", key: "opacity", label: "Opacity", default: 0.3, min: 0, max: 1, step: 0.01 },
    ];
  }

  render(
    svg: SVGSVGElement,
    width: number,
    height: number,
    settings: Record<string, any>,
  ): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { level, color, opacity } = settings;

    const w = width;
    const h = height;
    const mx = w / 2;
    const my = h / 2;

    // Basic: main diagonals
    group.appendChild(this.createLine(0, 0, w, h, color, opacity));
    group.appendChild(this.createLine(w, 0, 0, h, color, opacity));

    if (level === "mid" || level === "full") {
      // Mid: diagonals from midpoints of edges to opposite corners
      group.appendChild(this.createLine(mx, 0, 0, h, color, opacity * 0.7));
      group.appendChild(this.createLine(mx, 0, w, h, color, opacity * 0.7));
      group.appendChild(this.createLine(mx, h, 0, 0, color, opacity * 0.7));
      group.appendChild(this.createLine(mx, h, w, 0, color, opacity * 0.7));

      group.appendChild(this.createLine(0, my, w, 0, color, opacity * 0.7));
      group.appendChild(this.createLine(0, my, w, h, color, opacity * 0.7));
      group.appendChild(this.createLine(w, my, 0, 0, color, opacity * 0.7));
      group.appendChild(this.createLine(w, my, 0, h, color, opacity * 0.7));
    }

    if (level === "full") {
      // Full: quarter-point connections
      const qx = w / 4;
      const qy = h / 4;

      // Horizontals and verticals through quarter points
      group.appendChild(this.createLine(qx, 0, qx, h, color, opacity * 0.4));
      group.appendChild(this.createLine(w - qx, 0, w - qx, h, color, opacity * 0.4));
      group.appendChild(this.createLine(0, qy, w, qy, color, opacity * 0.4));
      group.appendChild(this.createLine(0, h - qy, w, h - qy, color, opacity * 0.4));

      // Center lines
      group.appendChild(this.createLine(mx, 0, mx, h, color, opacity * 0.3));
      group.appendChild(this.createLine(0, my, w, my, color, opacity * 0.3));
    }
  }
}
